import { Combobox, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFormValidation, useInputLabel, useInputValue } from '@/form-control'
import { useWindowWidth } from '@/hooks'

import { cn } from '@/utils/cn'

import Loading from '@/components/Loading'

import { DeepPartial, FormKey, FormWrapperInputOptions } from '../FormWrapper'
import { ISelectOption, OptionsListConfig, useOptionsList } from '../hooks'

export type SelectFieldProps<FormValuesType> = {
    options?: ISelectOption[]
    optionsListConfig?: OptionsListConfig
    createNewOptionFromQuery?: (queryString: string) => Promise<void>
    direction?: 'up' | 'down'
    // Inside FormWrapper
    field?: FormKey<FormValuesType>
    formValues?: DeepPartial<FormValuesType>
    setFormValues?: React.Dispatch<React.SetStateAction<any>>
    formOptions?: FormWrapperInputOptions<any>
    // Standalone
    value?: string
    onChange?: (value: string) => void
    //
    label?: string | boolean
    containerClass?: string
    labelClass?: string
    inputClass?: string
    inputFieldName?: string
    placeholder?: string
} & Omit<
    React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>,
    'value' | 'onChange'
>

export default function SelectField<FormValuesType = any>({
    options,
    optionsListConfig,
    createNewOptionFromQuery,
    direction = 'down',

    // Inside FormWrapper
    field,
    formValues,
    setFormValues,
    formOptions,
    // Standalone
    value,
    onChange,
    //
    label,
    containerClass,
    labelClass,
    inputClass,
    inputFieldName,
    placeholder,
    ...rest
}: SelectFieldProps<FormValuesType>) {
    const windowWidth = useWindowWidth()
    const { inputLabel } = useInputLabel(label, field, formOptions)
    const { inputValue, handleChange } = useInputValue<string>(
        value,
        onChange,
        field,
        formValues,
        setFormValues,
        formOptions
    )
    const { isValid, ValidationPrompt } = useFormValidation(formValues ?? {}, inputValue, field, formOptions)

    const { selectOptions } = useOptionsList({ options, optionsListConfig })
    const [isMobile, setIsMobile] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; bottom: number; left: number; width: number } | null>(null)

    useEffect(() => {
        if (windowWidth >= 640) {
            setIsMobile(false)
        }
    }, [windowWidth])

    // Update dropdown position when button position changes
    useEffect(() => {
        if (buttonRef.current && !isMobile) {
            const updatePosition = () => {
                const rect = buttonRef.current?.getBoundingClientRect()
                if (rect) {
                    setDropdownPosition({
                        top: rect.bottom,
                        bottom: window.innerHeight - rect.top,
                        left: rect.left,
                        width: rect.width,
                    })
                }
            }
            updatePosition()
            window.addEventListener('scroll', updatePosition, true)
            window.addEventListener('resize', updatePosition)
            return () => {
                window.removeEventListener('scroll', updatePosition, true)
                window.removeEventListener('resize', updatePosition)
            }
        }
    }, [isMobile])

    if (!selectOptions) return <Loading size={24} />

    if (isMobile) {
        return (
            <div className={cn('field-container', containerClass)}>
                {inputLabel && (
                    <label className={cn('field-label', labelClass)}>
                        {inputLabel} {rest?.required && <span className='text-red-500'>*</span>}
                    </label>
                )}
                <div className='relative'>
                    <select
                        {...rest}
                        className={cn(
                            'field-input appearance-none bg-white pr-10',
                            inputValue ? 'text-neutral-900' : 'text-neutral-400',
                            inputClass,
                            isValid ? '' : 'ring-amber-600 focus-within:ring-amber-600'
                        )}
                        value={inputValue}
                        onChange={(e) => handleChange(e.target.value)}
                        name={inputFieldName ? inputFieldName : field}
                    >
                        {!inputValue && (
                            <option value='' disabled>
                                {placeholder ?? 'Select...'}
                            </option>
                        )}
                        {selectOptions?.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.text}
                            </option>
                        ))}
                    </select>

                    <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                        <ChevronDownIcon className='w-5 h-5 text-neutral-500' aria-hidden='true' />
                    </div>
                </div>
                <ValidationPrompt />
            </div>
        )
    }

    return (
        <div className={cn('field-container', containerClass)}>
            {inputLabel && (
                <label className={cn('field-label', labelClass)}>
                    {inputLabel} {rest?.required && <span className='text-red-500'>*</span>}
                </label>
            )}
            <Combobox
                value={selectOptions?.find((option) => option.value === inputValue) ?? ''}
                onChange={(option: ISelectOption) => handleChange(option.value)}
            >
                {({ open }) => (
                    <>
                        <Combobox.Button
                            ref={buttonRef}
                            className={cn(
                                'flex items-center justify-between h-10 px-3 field-input',
                                isValid ? '' : 'ring-amber-600 focus-within:ring-amber-600'
                            )}
                        >
                            <span className={cn('truncate', inputValue ? '' : 'text-neutral-400')}>
                                {inputValue
                                    ? selectOptions?.find((option) => option.value === inputValue)?.text
                                    : (placeholder ?? 'Select...')}
                            </span>
                            <ChevronDownIcon className='w-5 h-5 text-neutral-500 shrink-0' aria-hidden='true' />
                        </Combobox.Button>
                        {open &&
                            dropdownPosition &&
                            createPortal(
                                <Transition
                                    show={open}
                                    as='div'
                                    className='fixed'
                                    style={{
                                        ...(direction === 'up'
                                            ? { bottom: `${dropdownPosition.bottom}px` }
                                            : { top: `${dropdownPosition.top}px` }),
                                        left: `${dropdownPosition.left}px`,
                                        width: `${dropdownPosition.width}px`,
                                        zIndex: 9999,
                                    }}
                                    enter='transition ease-out duration-100'
                                    enterFrom='opacity-0 scale-95'
                                    enterTo='opacity-100 scale-100'
                                    leave='transition ease-in duration-75'
                                    leaveFrom='opacity-100 scale-100'
                                    leaveTo='opacity-0 scale-95'
                                >
                                    <Combobox.Options
                                        static
                                        className='py-2 my-2 overflow-auto max-h-64 text-base rounded-lg border border-neutral-100 shadow-xl bg-white focus:outline-none'
                                    >
                                        {selectOptions?.map((option) => (
                                            <Combobox.Option
                                                key={option.value}
                                                value={option}
                                                className={({ active, selected }) =>
                                                    cn(
                                                        'relative cursor-default select-none py-2 px-3 text-neutral-700',
                                                        active && 'bg-brand/60 text-white',
                                                        selected && 'bg-brand text-white'
                                                    )
                                                }
                                            >
                                                {option.text}
                                            </Combobox.Option>
                                        ))}
                                    </Combobox.Options>
                                </Transition>,
                                document.body
                            )}
                    </>
                )}
            </Combobox>
            <ValidationPrompt />
        </div>
    )
}
